import React from 'react';
import PropTypes from 'prop-types';
import { DEFAULT_PAGE_SIZE_OPTIONS } from './tableTypes';
import './Table.css';

/**
 * TablePagination Component
 * Reusable pagination controls for tables
 */
const TablePagination = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showPageSizeSelector = true,
  showPageInfo = true,
  onPageChange,
  onPageSizeChange
}) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirst = () => {
    onPageChange(1);
  };

  const handleLast = () => {
    onPageChange(totalPages);
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    onPageSizeChange(newPageSize);
  };

  return (
    <div className="table-pagination">
      <div className="pagination-info">
        {showPageSizeSelector && (
          <div className="page-size-selector">
            <label htmlFor="page-size">Rows per page:</label>
            <select
              id="page-size"
              value={pageSize}
              onChange={handlePageSizeChange}
              className="page-size-select"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        )}
        
        {showPageInfo && (
          <span className="page-info">
            Showing {startItem} - {endItem} of {totalItems}
          </span>
        )}
      </div>

      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-btn pagination-btn-first"
          onClick={handleFirst}
          disabled={currentPage === 1}
          aria-label="First page"
          title="First page"
        >
          ««
        </button>
        
        <button
          type="button"
          className="pagination-btn pagination-btn-prev"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          aria-label="Previous page"
          title="Previous page"
        >
          «
        </button>
        
        <span className="page-number">
          Page {currentPage} of {totalPages || 1}
        </span>
        
        <button
          type="button"
          className="pagination-btn pagination-btn-next"
          onClick={handleNext}
          disabled={currentPage === totalPages || totalPages === 0}
          aria-label="Next page"
          title="Next page"
        >
          »
        </button>
        
        <button
          type="button"
          className="pagination-btn pagination-btn-last"
          onClick={handleLast}
          disabled={currentPage === totalPages || totalPages === 0}
          aria-label="Last page"
          title="Last page"
        >
          »»
        </button>
      </div>
    </div>
  );
};

TablePagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  showPageSizeSelector: PropTypes.bool,
  showPageInfo: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func
};

export default TablePagination;
